import { translate } from '../../i18n/index.js';
import { FormEvent, useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AtomFitLogo, Spinner } from '../../components/design/AtomFitUI.js';
import { useAuth } from '../../hooks/useAuth.js';
import { authApi } from '../../api/auth.api.js';

type Step =
  | 'email'
  | 'password'
  | 'magic-link'
  | 'magic-sent'
  | 'forgot'
  | 'forgot-sent'
  | 'register'
  | 'verification-sent';

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 0,
  color: 'var(--af-accent)',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
};
const mutedLinkBtn: React.CSSProperties = { ...linkBtn, color: 'var(--af-muted)' };

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const passwordResetSuccess = searchParams.get('passwordReset') === '1';
  const referralCode = searchParams.get('ref')?.trim() || undefined;
  const redirectTo = getSafeRedirect(searchParams.get('redirect'));
  const isBookingRedirect = redirectTo?.startsWith('/checkout') ?? false;
  const { login, register } = useAuth();

  const [step, setStep] = useState<Step>(
    searchParams.get('step') === 'password' && searchParams.get('email') ? 'password' : 'email',
  );
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  useEffect(() => {
    const update = () =>
      setResendSeconds(Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)));
    update();
    if (resendAvailableAt <= Date.now()) return;
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [resendAvailableAt]);
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [magicPhoneRequired, setMagicPhoneRequired] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  function reset() {
    setStep('email');
    setVerificationSent(false);
    setResendAvailableAt(0);
    setEmail('');
    setPassword('');
    setPhone('');
    setMagicPhoneRequired(false);
    setFirstName('');
    setLastName('');
    setError(null);
    setShowPassword(false);
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await authApi.lookup(email);
      setMagicPhoneRequired(data.method === 'magic-link' && data.phoneRequired);
      setStep(data.method);
    } catch {
      setError(translate('pages.public.login.handle_email_submit.set_error.error_email'));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(email, password);
      void navigate(redirectTo ?? (result.type === 'admin' ? '/admin' : '/gyms'), {
        replace: true,
      });
    } catch (err: unknown) {
      const reason = (
        err as { response?: { data?: { error?: { details?: { reason?: string } } } } }
      ).response?.data?.error?.details?.reason;
      if (reason === 'EMAIL_VERIFICATION_REQUIRED') {
        setPassword('');
        setStep('verification-sent');
        return;
      }
      setError(translate('pages.public.login.handle_password_submit.set_error.password'));
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (magicPhoneRequired && !isValidPhoneNumber(phone)) {
      setError(translate('pages.public.login.handle_magic_link.set_error.phone'));
      setLoading(false);
      return;
    }
    try {
      await authApi.requestMagicLink(
        email,
        redirectTo ?? undefined,
        magicPhoneRequired ? phone : undefined,
      );
      setStep('magic-sent');
    } catch {
      setError(
        magicPhoneRequired
          ? translate('pages.public.login.handle_magic_link.set_error.phone_2')
          : translate('pages.public.login.handle_magic_link.set_error.error'),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.requestPasswordReset(email);
      setStep('forgot-sent');
    } catch {
      setError(translate('pages.public.login.handle_forgot.set_error.error_email'));
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (!isValidPhoneNumber(phone)) {
      setError(translate('pages.public.login.handle_register.set_error.phone'));
      setLoading(false);
      return;
    }
    try {
      const registered = await register(
        email,
        password,
        firstName || undefined,
        lastName || undefined,
        phone,
        referralCode,
        redirectTo ?? undefined,
      );
      setEmail(registered.email);
      setPassword('');
      setVerificationSent(false);
      setResendAvailableAt(Date.now() + 60_000);
      setStep('verification-sent');
    } catch (err: unknown) {
      const response = (
        err as {
          response?: {
            status?: number;
            data?: { error?: { details?: { reason?: string; phoneRequired?: boolean } } };
          };
        }
      )?.response;
      if (response?.status === 403) {
        setError(translate('pages.public.login.handle_register.set_error.error'));
        return;
      }
      setError(
        response?.status === 409
          ? translate('pages.public.login.handle_register.set_error.email')
          : translate('pages.public.login.handle_register.set_error.error_2'),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    if (Date.now() < resendAvailableAt) {
      setError(translate('email_verification.cooldown'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authApi.resendEmailVerification(email, redirectTo ?? undefined);
      setVerificationSent(true);
      setResendAvailableAt(Date.now() + 60_000);
    } catch {
      setError(translate('email_verification.send_error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="af-screen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px 48px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -200,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 700,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(184,160,32,0.055) 0%, transparent 65%)',
        }}
      />

      <section style={{ width: 'min(420px, 100%)', position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <Link className="af-logo" to="/" style={{ fontSize: 20, justifyContent: 'center' }}>
            <AtomFitLogo />
          </Link>
        </div>

        <div
          className="af-card"
          style={{ padding: '32px 32px 28px', boxShadow: '0 18px 42px rgba(13,13,13,0.12)' }}
        >
          {step === 'verification-sent' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h1 style={{ fontSize: 22, margin: 0 }}>
                {translate('email_verification.pending_title')}
              </h1>
              <p style={{ color: 'var(--af-muted)', margin: 0 }}>
                {translate('email_verification.pending', { email })}
              </p>
              {verificationSent && <p role="status">{translate('email_verification.sent')}</p>}
              {error && (
                <div className="af-error" role="alert">
                  {error}
                </div>
              )}
              <button
                className="af-primary-btn"
                disabled={loading || resendSeconds > 0}
                onClick={() => void handleResendVerification()}
              >
                {loading ? (
                  <Spinner />
                ) : resendSeconds > 0 ? (
                  translate('email_verification.resend_wait', { seconds: resendSeconds })
                ) : (
                  translate('email_verification.resend')
                )}
              </button>
              <button
                style={linkBtn}
                onClick={() => {
                  setError(null);
                  setStep('password');
                }}
              >
                {translate('email_verification.login')}
              </button>
              <button style={mutedLinkBtn} onClick={reset}>
                {translate('email_verification.change_email')}
              </button>
            </div>
          )}
          {/* ── Email step ── */}
          {step === 'email' && (
            <>
              {passwordResetSuccess && (
                <div
                  style={{
                    background: 'var(--af-accent-soft)',
                    border: '1px solid var(--af-accent-glow)',
                    borderRadius: 0,
                    padding: '10px 14px',
                    marginBottom: 20,
                    fontSize: 13,
                    color: 'var(--af-accent)',
                    fontWeight: 600,
                  }}
                >
                  {translate('pages.public.login.login.div.password_create')}
                </div>
              )}
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {isBookingRedirect
                    ? translate('pages.public.login.login.h1.email')
                    : translate('pages.public.login.login.h1.heading')}
                </h1>
                <p style={{ fontSize: 14, color: 'var(--af-muted)', lineHeight: 1.6, margin: 0 }}>
                  {isBookingRedirect
                    ? translate('pages.public.login.login.p.booking_email')
                    : translate('pages.public.login.login.p.email')}
                </p>
              </div>
              <form
                onSubmit={handleEmailSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.email')}

                  <input
                    ref={emailRef}
                    className="af-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={translate(
                      'pages.public.login.login.input_input.placeholder_content',
                    )}
                    required
                    autoFocus
                  />
                </label>
                {error && <div className="af-error">{error}</div>}
                <button
                  className="af-primary-btn"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <Spinner /> {translate('pages.public.login.login.button_primary_btn.label')}
                    </>
                  ) : (
                    translate('pages.public.login.login.button_primary_btn.label_2')
                  )}
                </button>
              </form>
              <div
                style={{
                  marginTop: 20,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--af-muted)',
                }}
              >
                {translate('pages.public.login.login.div.copy')}{' '}
                <button onClick={() => setStep('register')} style={linkBtn}>
                  {translate('pages.public.login.login.button.registration')}
                </button>
              </div>
            </>
          )}

          {/* ── Password step ── */}
          {step === 'password' && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {translate('pages.public.login.login.h1.login')}
                </h1>
                <button
                  onClick={reset}
                  style={{
                    ...mutedLinkBtn,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{'←'}</span> {email}
                </button>
              </div>
              <form
                onSubmit={handlePasswordSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.password')}

                  <span style={{ position: 'relative' }}>
                    <input
                      className="af-input af-mono"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={'••••••••'}
                      required
                      minLength={8}
                      style={{ paddingRight: 44 }}
                      autoFocus
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword
                          ? translate('pages.public.login.login.button.aria_label_password')
                          : translate('pages.public.login.login.button.aria_label_password_2')
                      }
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 0,
                        color: 'var(--af-muted)',
                        padding: 2,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"
                          stroke="currentColor"
                          strokeWidth="1.4"
                        />

                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
                        {showPassword && (
                          <line
                            x1="2"
                            y1="2"
                            x2="14"
                            y2="14"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                          />
                        )}
                      </svg>
                    </button>
                  </span>
                </label>
                <div style={{ textAlign: 'right', marginTop: -6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setPassword('');
                      setError(null);
                      setStep('forgot');
                    }}
                    style={{ ...mutedLinkBtn, fontSize: 12 }}
                  >
                    {translate('pages.public.login.login.button.password')}
                  </button>
                </div>
                {error && <div className="af-error">{error}</div>}
                <button
                  className="af-primary-btn"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <Spinner /> {translate('pages.public.login.login.button_primary_btn.label_3')}
                    </>
                  ) : (
                    translate('pages.public.login.login.button_primary_btn.login')
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Magic link step ── */}
          {step === 'magic-link' && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {translate('pages.public.login.login.h1.email_2')}
                </h1>
                <button
                  onClick={reset}
                  style={{
                    ...mutedLinkBtn,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{'←'}</span> {email}
                </button>
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: 'var(--af-muted)',
                  lineHeight: 1.6,
                  margin: '0 0 20px',
                }}
              >
                {magicPhoneRequired
                  ? translate('pages.public.login.login.p.phone_sent')
                  : translate('pages.public.login.login.p.email_2')}
              </p>
              <form
                onSubmit={handleMagicLink}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                {magicPhoneRequired && (
                  <label className="af-label">
                    {translate('pages.public.login.login.label_label.phone')}

                    <PhoneInput
                      className="af-phone-input"
                      defaultCountry="CZ"
                      international
                      countryCallingCodeEditable
                      value={phone}
                      onChange={(value) => setPhone(value ?? '')}
                      placeholder={'+420 777 123 456'}
                      required
                      autoFocus
                    />
                  </label>
                )}
                {error && <div className="af-error">{error}</div>}
                <button className="af-primary-btn" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner /> {translate('pages.public.login.login.button_primary_btn.label_4')}
                    </>
                  ) : (
                    translate('pages.public.login.login.button_primary_btn.label_5')
                  )}
                </button>
              </form>
              <div
                style={{
                  marginTop: 20,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--af-muted)',
                }}
              >
                {translate('pages.public.login.login.div.benefit')}{' '}
                <button onClick={() => setStep('register')} style={linkBtn}>
                  {translate('pages.public.login.login.button.registration_2')}
                </button>
              </div>
            </>
          )}

          {/* ── Magic sent ── */}
          {step === 'magic-sent' && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  background: 'var(--af-accent-soft)',
                  border: '2px solid var(--af-accent-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                  <rect
                    x="2"
                    y="6"
                    width="22"
                    height="14"
                    rx="3"
                    stroke="var(--af-accent)"
                    strokeWidth="1.6"
                  />

                  <path
                    d="M2 9l11 7 11-7"
                    stroke="var(--af-accent)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px' }}>
                {translate('pages.public.login.login.h1.email_3')}
              </h1>
              <p
                style={{
                  color: 'var(--af-muted)',
                  fontSize: 14,
                  lineHeight: 1.7,
                  margin: '0 0 20px',
                }}
              >
                {translate('pages.public.login.login.p.description')}
                <strong style={{ color: 'var(--af-text)' }}>{email}</strong>{' '}
                {translate('pages.public.login.login.p.time')}
              </p>
              <button className="af-outline-btn" onClick={reset}>
                {translate('pages.public.login.login.button_outline_btn.label')}
              </button>
            </div>
          )}

          {/* ── Forgot password ── */}
          {step === 'forgot' && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {translate('pages.public.login.login.h1.password')}
                </h1>
                <p style={{ fontSize: 14, color: 'var(--af-muted)', lineHeight: 1.6, margin: 0 }}>
                  {translate('pages.public.login.login.p.password')}
                </p>
              </div>
              <form
                onSubmit={handleForgot}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.email_2')}

                  <input
                    className="af-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={translate(
                      'pages.public.login.login.input_input.placeholder_content_2',
                    )}
                    required
                    autoFocus
                  />
                </label>
                {error && <div className="af-error">{error}</div>}
                <button
                  className="af-primary-btn"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <Spinner /> {translate('pages.public.login.login.button_primary_btn.label_6')}
                    </>
                  ) : (
                    translate('pages.public.login.login.button_primary_btn.label_7')
                  )}
                </button>
              </form>
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button
                  onClick={() => {
                    setError(null);
                    setStep('email');
                  }}
                  style={mutedLinkBtn}
                >
                  {translate('pages.public.login.login.button.back')}
                </button>
              </div>
            </>
          )}

          {/* ── Forgot sent ── */}
          {step === 'forgot-sent' && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  background: 'var(--af-accent-soft)',
                  border: '2px solid var(--af-accent-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                  <rect
                    x="2"
                    y="6"
                    width="22"
                    height="14"
                    rx="3"
                    stroke="var(--af-accent)"
                    strokeWidth="1.6"
                  />

                  <path
                    d="M2 9l11 7 11-7"
                    stroke="var(--af-accent)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px' }}>
                {translate('pages.public.login.login.h1.email_4')}
              </h1>
              <p
                style={{
                  color: 'var(--af-muted)',
                  fontSize: 14,
                  lineHeight: 1.7,
                  margin: '0 0 20px',
                }}
              >
                {translate('pages.public.login.login.p.password_2')}
                <strong style={{ color: 'var(--af-text)' }}>{email}</strong>
                {translate('pages.public.login.login.p.time_validity')}
              </p>
              <button
                className="af-outline-btn"
                onClick={() => {
                  setError(null);
                  setStep('email');
                }}
              >
                {translate('pages.public.login.login.button_outline_btn.back')}
              </button>
            </div>
          )}

          {/* ── Register ── */}
          {step === 'register' && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {translate('pages.public.login.login.h1.create')}
                </h1>
                <p style={{ fontSize: 14, color: 'var(--af-muted)', lineHeight: 1.6, margin: 0 }}>
                  {translate('pages.public.login.login.p.booking')}
                </p>
              </div>
              {referralCode && (
                <div
                  style={{
                    background: 'var(--af-accent-soft)',
                    border: '1px solid var(--af-accent-glow)',
                    borderRadius: 0,
                    padding: '10px 14px',
                    marginBottom: 18,
                    fontSize: 13,
                    color: 'var(--af-accent)',
                    fontWeight: 700,
                  }}
                >
                  {translate('pages.public.login.login.div.copy_2')}
                </div>
              )}
              <form
                onSubmit={handleRegister}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="af-label">
                    {translate('pages.public.login.login.label_label.name')}

                    <input
                      className="af-input"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={translate(
                        'pages.public.login.login.input_input.placeholder_content_3',
                      )}
                    />
                  </label>
                  <label className="af-label">
                    {translate('pages.public.login.login.label_label.last_name')}

                    <input
                      className="af-input"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={translate(
                        'pages.public.login.login.input_input.placeholder_create',
                      )}
                    />
                  </label>
                </div>
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.email_3')}

                  <input
                    className="af-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={translate(
                      'pages.public.login.login.input_input.placeholder_content_4',
                    )}
                    required
                    autoFocus
                  />
                </label>
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.phone_2')}

                  <PhoneInput
                    className="af-phone-input"
                    defaultCountry="CZ"
                    international
                    countryCallingCodeEditable
                    value={phone}
                    onChange={(value) => setPhone(value ?? '')}
                    placeholder={'+420 777 123 456'}
                    required
                  />
                </label>
                <label className="af-label">
                  {translate('pages.public.login.login.label_label.password_2')}

                  <span style={{ position: 'relative' }}>
                    <input
                      className="af-input af-mono"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={'••••••••'}
                      required
                      minLength={8}
                      style={{ paddingRight: 44 }}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword
                          ? translate('pages.public.login.login.button.aria_label_password_3')
                          : translate('pages.public.login.login.button.aria_label_password_4')
                      }
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 0,
                        color: 'var(--af-muted)',
                        padding: 2,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"
                          stroke="currentColor"
                          strokeWidth="1.4"
                        />

                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
                        {showPassword && (
                          <line
                            x1="2"
                            y1="2"
                            x2="14"
                            y2="14"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                          />
                        )}
                      </svg>
                    </button>
                  </span>
                </label>
                {error && <div className="af-error">{error}</div>}
                <button
                  className="af-primary-btn"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <Spinner />{' '}
                      {translate('pages.public.login.login.button_primary_btn.registration')}
                    </>
                  ) : (
                    translate('pages.public.login.login.button_primary_btn.create')
                  )}
                </button>
              </form>
              <div
                style={{
                  marginTop: 20,
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'var(--af-muted)',
                }}
              >
                {translate('pages.public.login.login.div.copy_3')}{' '}
                <button
                  onClick={() => {
                    setError(null);
                    setStep('email');
                  }}
                  style={linkBtn}
                >
                  {translate('pages.public.login.login.button.label')}
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function getSafeRedirect(value: string | null) {
  if (!value?.startsWith('/')) return null;
  if (value.startsWith('//') || value.includes('\\')) return null;
  return value;
}
